import { func, number, bool, oneOf } from 'prop-types';

import TrendsPropType from 'propTypes/trends';
import getEntityKey from 'helpers/getEntityKey';
import PathWithFilledGaps from 'components/data-visualization/PathWithFilledGaps/index';
import MissingDataTrendLines from 'components/data-visualization/MissingDataTrendLines/index';

export default TrendLines;

/**
 * TrendLines renders the trend lines in the drawing area of the LineChart.
 * They can optionally fill the gaps in a trend with dashed lines, and fill the
 * removed outliers at the end of a trend with a straight dashed line.
 *
 * @param {object} props React Props
 * @param {function} props.colorScale Ordinal scale used to get trend line colors
 * @param {boolean} props.hasDashedGaps Should these trend lines fill the gaps in their data with dashed lines?
 * @param {boolean} props.hasInterpolatedTrend Should these trend lines fill the removed outliers with a straight dashed line?
 * @param {number} props.height The height of the drawing area
 * @param {function} props.path Configured D3.js line renderer
 * @param {array} props.trends The trend data to render
 * @param {('value'|'position')} props.valueKey The key to use to access the values for these trends.
 * @param {number} props.width The width of the drawing area
 * @param {function} props.x The scale for the horizontal (x) axis
 */
function TrendLines(props) {
  const {
    colorScale,
    hasDashedGaps,
    hasInterpolatedTrend,
    height,
    path,
    trends,
    valueKey = 'value',
    width,
    x,
  } = props;

  return (
    <>
      {trends.map((trend) => (
        <PathWithFilledGaps
          key={`filled-gaps-path-${getEntityKey(trend)}`}
          trend={trend}
          path={path}
          x={x}
          width={width}
          height={height}
          dateKey="periodKey"
          valueKey={valueKey}
          showGapLines={hasDashedGaps}
          stroke={colorScale(trend)}
          strokeWidth={2}
          fill="none"
        />
      ))}

      {hasInterpolatedTrend && (
        <MissingDataTrendLines
          trends={trends}
          colorScale={colorScale}
          dataKey={valueKey}
          path={path}
        />
      )}
    </>
  );
}

TrendLines.propTypes = {
  colorScale: func.isRequired,
  hasDashedGaps: bool.isRequired,
  hasInterpolatedTrend: bool.isRequired,
  height: number.isRequired,
  path: func.isRequired,
  trends: TrendsPropType.isRequired,
  valueKey: oneOf(['value', 'position']),
  width: number.isRequired,
  x: func.isRequired,
};
